// /administracion/equipo — Administración · Equipo y accesos (handoff
// autogestión 2026-10-29 §3, plan 2026-10-30 §2.3, T-jre9): la pantalla
// de facility sobre el roster de auth-svc, las invitaciones pendientes y
// los alcances por tienda de core-api. routes/Equipo.tsx (la tabla vieja)
// sigue sirviendo a postventa/arriendos sin cambios; qué pantalla se
// monta lo decide App.tsx por la receta (adminModule), nunca por slug.
//
// Un panel con tres tabs. Miembros: Persona · Rol · Acceso · Última
// actividad · Estado · «Editar acceso» / «Desactivar» (danger) o
// «Reactivar». Invitaciones pendientes: Correo · Rol · Acceso · Enviada ·
// Estado · «Reenviar» / «Revocar» (danger). Roles: Rol · Qué ve · Quién
// lo puede invitar, derivada de la receta y de
// INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER. Un solo botón primario,
// «Invitar persona»; a su lado el secundario «Invitar en lote» (T-b7bh:
// planilla → preview → commit sobre /organizations/me/invitations/batch),
// con la misma capacidad.
//
// Lo que el handoff dibuja y el backend no tiene todavía no se muestra
// (regla «hide unusable actions»): el campo «Mensaje» del correo, el
// estado «Correo rebotó» (sin delivery_status ni webhook de Resend), el
// cambio de rol y el alcance de tiendas de una invitación pendiente.
//
// Quién ve qué: la ruta la monta App.tsx para los roles que leen el
// roster (isTeamRosterReadRole); aquí sólo se apaga la consulta de
// alcances para un rol sin lectura, así un montaje sin guarda nunca
// dispara un 403. Invitar sigue el gate de POST /organizations/me/
// invitations (isInvitationCreateRole o administrador de plataforma).
// Desactivar, reactivar y editar acceso siguen el de quien administra la
// organización según la receta (canAdministerOrg, espejo de
// requireOrgAdmin y de la lectura de política de auth-svc), y nunca se
// ofrecen sobre la fila propia, la del dueño ni la de otro administrador
// (el servidor también las rechaza). Reenviar y revocar: quien administra
// o quien creó la invitación.

import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  isInvitationCreateRole,
  isTeamRosterReadRole,
  type OrgInvitationListItem,
  type OrgMember,
  type MemberScopeItem,
} from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageError } from "../../components/PageError";
import { PageHeader } from "../../components/PageHeader";
import { PageSkeleton } from "../../components/PageSkeleton";
import { Panel } from "../../components/Panel";
import { Skel } from "../../components/Skel";
import { SkelTable } from "../../components/skeleton/SkelTable";
import { Tabs, tabPanelProps } from "../../components/Tabs";
import { AccesoDrawer } from "../../features/administracion/equipo/AccesoDrawer";
import { InvitarDrawer } from "../../features/administracion/equipo/InvitarDrawer";
import { InvitarLoteDrawer } from "../../features/administracion/equipo/InvitarLoteDrawer";
import {
  accessLabel,
  batchToast,
  canActOnInvitation,
  canChangeMemberState,
  equipoWords,
  filterInvitations,
  filterMembers,
  fmtEnviada,
  invitationAccessLabel,
  invitationActionErrorMessage,
  invitationEmailLabel,
  inviteToast,
  isStoreScopedRole,
  lastActivityLabel,
  memberInitials,
  membershipErrorMessage,
  RESEND_EMAIL_FAILED,
  rolePillClass,
  rolesTable,
  storePickerMode,
  type BatchOutcome,
  type InviteOutcome,
  type Me,
} from "../../features/administracion/equipo/model";
import {
  invalidateInvitaciones,
  useInvitacionesEquipo,
  useMemberScopes,
  usePatchMembership,
  useResendInvitation,
  useRevokeInvitation,
} from "../../features/administracion/equipo/queries";
import { RefreshIcon } from "../../features/administracion/equipo/RefreshIcon";
import { UploadIcon } from "../../features/administracion/equipo/UploadIcon";
import { adminWords, roleLabelFor } from "../../lib/admin/words";
import {
  useAssignableAssets,
  useBrandAffiliations,
  useMyOrganization,
  useRecipe,
  useResolveScopes,
  useRoster,
} from "../../lib/api/queries";
import { useSession } from "../../lib/auth/useSession";
import { canAdministerOrg } from "../../lib/capabilities";
import { useQueryView } from "../../lib/loading/useQueryView";
import { ADMIN_BASE_PATH } from "../../lib/nav";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";
import { gateOf, RefreshNotices, SlowNotice } from "../queryViewKit";

export const EQUIPO_PATH = `${ADMIN_BASE_PATH}/equipo`;

type Tab = "miembros" | "invitaciones" | "roles";

const TABS_ID = "equipo";
const NO_MEMBERS: OrgMember[] = [];
const NO_INVITATIONS: OrgInvitationListItem[] = [];
const NO_SCOPES: MemberScopeItem[] = [];
const NO_IDS: string[] = [];

// Cabeceras fijas para que el esqueleto pinte la misma fila que los datos.
const MEMBER_HEADERS = ["Persona", "Rol", "Acceso", "Última actividad", "Estado", ""];

function uniqIds(ids: ReadonlyArray<string | null | undefined>): string[] {
  return [...new Set(ids.filter((id): id is string => typeof id === "string" && id.length > 0))];
}

export default function Equipo() {
  const session = useSession();
  const role = session?.organization.role;
  const platformAdmin = session?.user.platformAdmin === true;
  const me: Me = { userId: session?.user.id, role, platformAdmin };

  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  const rosterQuery = useRoster();
  const invitacionesQuery = useInvitacionesEquipo();
  // Lee los alcances: el gate de GET /organizations/me/member-scopes.
  const canReadScopes = platformAdmin || (role !== undefined && isTeamRosterReadRole(role));
  const scopesQuery = useMemberScopes(canReadScopes);
  const orgQuery = useMyOrganization();
  // Invita: el gate de POST /organizations/me/invitations. También abre
  // GET /assets/assignable, la fuente de los selectores de tiendas y del
  // conteo de tiendas por zona de la columna Acceso.
  const canInvite = platformAdmin || (role !== undefined && isInvitationCreateRole(role));
  const storesQuery = useAssignableAssets(canInvite);
  // Administra: el gate de PATCH /auth/memberships/:id, de PUT …/scope y
  // de revocar/reenviar sin haber invitado.
  const canManage = canAdministerOrg(recipe, role, { platformAdmin });
  // Ve la organización entera (alcance `organization` en la receta o
  // administrador de plataforma): sólo entonces GET /assets/assignable
  // trae todas las tiendas y sirve para contar las de cada zona.
  const callerOrgScoped =
    platformAdmin || recipe?.memberRoles.find((r) => r.key === role)?.scopeType === "organization";
  const toast = useToast();
  const qc = useQueryClient();

  const words = adminWords(recipe);
  const w = useMemo(() => equipoWords(recipe), [recipe]);

  const [tab, setTab] = useState<Tab>("miembros");
  const [q, setQ] = useState("");
  const [invitar, setInvitar] = useState(false);
  const [lote, setLote] = useState(false);
  const [acceso, setAcceso] = useState<OrgMember | null>(null);
  // Ids (membresía o invitación) con una acción en vuelo: cada fila queda
  // deshabilitada hasta que vuelve la suya (precedente: Contratistas).
  const [enVuelo, setEnVuelo] = useState<ReadonlySet<string>>(() => new Set());

  const patch = usePatchMembership();
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();

  // Las cuatro consultas son independientes y la pantalla necesita las
  // cuatro, así que se pliegan en un solo gate: el primer error gana,
  // luego el skeleton, y la cabecera pinta desde el primer render.
  const gate = gateOf([
    useQueryView(recipeQuery),
    useQueryView(rosterQuery),
    useQueryView(invitacionesQuery),
    useQueryView(scopesQuery),
  ]);

  function retryAll() {
    for (const query of [recipeQuery, rosterQuery, invitacionesQuery, scopesQuery]) {
      if (query.isError || query.isPending) void query.refetch();
    }
  }

  const ready = gate.kind === "ready";
  const members = ready ? gate.data[1].members : NO_MEMBERS;
  const invitations = ready ? gate.data[2].invitations : NO_INVITATIONS;
  const scopeItems = ready ? gate.data[3].items : NO_SCOPES;

  // Espejo en cliente del filtro view=roster: sólo roles que la receta
  // declara, para el conteo, la tabla y la búsqueda.
  const memberRoleKeys = useMemo(
    () => new Set(recipe?.memberRoles.map((r) => r.key) ?? []),
    [recipe]
  );
  const visibleMembers = useMemo(
    () => members.filter((m) => memberRoleKeys.has(m.role)),
    [members, memberRoleKeys]
  );
  const scopeByUser = useMemo(
    () => new Map(scopeItems.map((s) => [s.userId, s] as const)),
    [scopeItems]
  );

  // Nombres de zonas, tiendas y marcas por id (POST /assets/resolve), con
  // las marcas de la org encima cuando el caller las recibe.
  const resolveRequest = useMemo(
    () => ({
      brandIds: uniqIds([
        ...visibleMembers.map((m) => m.scopeBrandId),
        ...invitations.map((i) => i.scopeBrandId),
      ]),
      assetGroupIds: uniqIds([
        ...scopeItems.flatMap((s) => s.assetGroupIds),
        ...visibleMembers.map((m) => m.scopeAssetGroupId),
      ]),
      assetIds: uniqIds([
        ...scopeItems.flatMap((s) => s.assetIds),
        ...visibleMembers.map((m) => m.scopeAssetId),
      ]),
    }),
    [visibleMembers, invitations, scopeItems]
  );
  const resolved = useResolveScopes(resolveRequest);
  const orgBrands = orgQuery.data?.brands;
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of resolved.data?.brands ?? []) map.set(b.id, b.name);
    for (const g of resolved.data?.assetGroups ?? []) map.set(g.id, g.name);
    for (const a of resolved.data?.assets ?? []) map.set(a.id, a.name);
    for (const b of orgBrands ?? []) map.set(b.id, b.name);
    return map;
  }, [resolved.data, orgBrands]);
  // Un supervisor también recibe la lista, pero acotada a su alcance: para
  // él el conteo saldría parcial, así que la celda cae a sólo la zona.
  const zoneStoreCount = useMemo(() => {
    if (!storesQuery.data || !callerOrgScoped) return null;
    const map = new Map<string, number>();
    for (const a of storesQuery.data.assets) {
      if (a.assetGroupId) map.set(a.assetGroupId, (map.get(a.assetGroupId) ?? 0) + 1);
    }
    return map;
  }, [storesQuery.data, callerOrgScoped]);

  // Marca de afiliación de los supervisores (ADR-0165): sólo para quien
  // puede editarla (alcance de organización) y en una org con más de una
  // marca; con una sola la rellena el servidor.
  const multiBrand = (orgBrands?.length ?? 0) > 1;
  const hasAffiliationRoles =
    recipe?.memberRoles.some((r) => r.brandIdentity === "affiliation") === true;
  const canEditAffiliation = canManage && callerOrgScoped && multiBrand && hasAffiliationRoles;
  const affiliations = useBrandAffiliations(canEditAffiliation);
  const affiliationByUser = useMemo(
    () => new Map((affiliations.data?.affiliations ?? []).map((a) => [a.userId, a.brandId])),
    [affiliations.data]
  );

  const shownMembers = useMemo(() => filterMembers(visibleMembers, q), [visibleMembers, q]);
  const shownInvitations = useMemo(() => filterInvitations(invitations, q), [invitations, q]);
  const roleRows = useMemo(() => (recipe ? rolesTable(recipe) : []), [recipe]);
  const showInvitationActions = invitations.some((i) => canActOnInvitation(i, me, canManage));

  function saved(message: string, tone: "ok" | "err" = "ok") {
    toast.show(message, tone, ADMIN_TOAST_OPTS);
  }

  function track(id: string, on: boolean) {
    setEnVuelo((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  // mutateAsync y no mutate(vars, { onSuccess }): en TanStack Query v5
  // los callbacks de `mutate` sólo corren para la última llamada, así
  // que dos filas pulsadas seguidas perderían un toast.
  async function cambiarEstado(m: OrgMember, desactivar: boolean) {
    if (!m.membershipId) return;
    track(m.membershipId, true);
    try {
      await patch.mutateAsync({
        membershipId: m.membershipId,
        body: { disabledAt: desactivar ? new Date().toISOString() : null },
      });
      saved(desactivar ? `${m.displayName} ya no puede entrar` : `${m.displayName} reactivado`);
    } catch (err) {
      saved(membershipErrorMessage(err), "err");
    } finally {
      track(m.membershipId, false);
    }
  }

  async function reenviar(inv: OrgInvitationListItem) {
    track(inv.id, true);
    try {
      const res = await resend.mutateAsync(inv.id);
      if (res.emailSent) saved(`Invitación reenviada a ${inv.email ?? ""}`.trim());
      else saved(RESEND_EMAIL_FAILED, "err");
    } catch (err) {
      saved(invitationActionErrorMessage(err), "err");
    } finally {
      track(inv.id, false);
    }
  }

  async function revocar(inv: OrgInvitationListItem) {
    track(inv.id, true);
    try {
      await revoke.mutateAsync(inv.id);
      saved("Invitación revocada");
    } catch (err) {
      saved(invitationActionErrorMessage(err), "err");
    } finally {
      track(inv.id, false);
    }
  }

  function invitacionesEnviadas(outcome: InviteOutcome) {
    const t = inviteToast(outcome);
    saved(t.message, t.tone);
    if (outcome.sent.length > 0) {
      invalidateInvitaciones(qc);
      setTab("invitaciones");
      setQ("");
    }
  }

  // El lote invalida desde su hook (useInvitationBatchCommit); aquí sólo
  // el toast y el cambio de tab, como tras «Invitar persona».
  function loteEnviado(outcome: BatchOutcome) {
    const t = batchToast(outcome);
    saved(t.message, t.tone);
    if (outcome.sent > 0) {
      setTab("invitaciones");
      setQ("");
    }
  }

  // Las tiendas directas del integrante abierto en «Editar acceso».
  const accesoAssetIds = useMemo(
    () =>
      acceso
        ? uniqIds([...(scopeByUser.get(acceso.userId)?.assetIds ?? []), acceso.scopeAssetId])
        : NO_IDS,
    [acceso, scopeByUser]
  );

  // Rol cargado y sin lectura: App.tsx ya redirige; aquí no hay nada que
  // mostrar y la consulta de alcances quedó apagada.
  if (role !== undefined && !canReadScopes) return null;

  function renderMiembros() {
    if (shownMembers.length === 0) {
      return q.trim() ? (
        <EmptyState title="Nadie coincide" description="Prueba con otro nombre o correo." />
      ) : (
        <EmptyState
          title="Sin miembros todavía"
          {...(canInvite && {
            description: "Invita a la primera persona con «Invitar persona».",
          })}
        />
      );
    }
    return (
      <table className="dtable">
        <caption className="sr-only">Miembros del equipo</caption>
        <thead>
          <tr>
            <th scope="col">Persona</th>
            <th scope="col">Rol</th>
            <th scope="col">Acceso</th>
            <th scope="col">Última actividad</th>
            <th scope="col">Estado</th>
            {canManage && (
              <th scope="col">
                <span className="sr-only">Acciones</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {shownMembers.map((m) => {
            const activo = m.disabledAt == null;
            const access = accessLabel(m, {
              recipe,
              scope: scopeByUser.get(m.userId),
              names,
              zoneStoreCount,
            });
            // PUT …/scope sólo encuentra integrantes activos (MEMBER_NOT_FOUND
            // sobre uno desactivado): el acceso se edita tras reactivar.
            const puedeEditar = canManage && activo && isStoreScopedRole(recipe, m.role);
            const puedeEstado = canManage && canChangeMemberState(recipe, m, me);
            const cambiando = m.membershipId ? enVuelo.has(m.membershipId) : false;
            return (
              <tr key={m.userId}>
                <td>
                  <span className="user-cell">
                    <span className="avatar" aria-hidden="true">
                      {memberInitials(m.displayName)}
                    </span>
                    <span className="cell-with-sub">
                      <span className="user-cell__name">{m.displayName}</span>
                      <span className="user-cell__sub mono">{m.email}</span>
                    </span>
                  </span>
                </td>
                <td>
                  <span className={rolePillClass(recipe, m.role)}>
                    {roleLabelFor(m.role, recipe)}
                  </span>
                </td>
                <td>
                  <span className="cell-meta">{access}</span>
                </td>
                <td>
                  <span className="cell-meta">{lastActivityLabel(m.lastSeenAt)}</span>
                </td>
                <td>
                  <span className={`st-pill st-pill--${activo ? "ok" : "idle"}`}>
                    <span className={`st-dot st-dot--${activo ? "ok" : "idle"}`} />
                    {activo ? "Activo" : "Desactivado"}
                  </span>
                </td>
                {canManage && (
                  <td className="cell-actions">
                    {puedeEditar && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        title={`Cambiar las ${w.assetManyLower} a las que accede ${m.displayName}`}
                        disabled={cambiando}
                        onClick={() => setAcceso(m)}
                      >
                        Editar acceso
                      </button>
                    )}
                    {puedeEstado &&
                      (activo ? (
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          title={`Quitar el acceso a ${m.displayName}; lo que registró se conserva`}
                          disabled={cambiando}
                          onClick={() => void cambiarEstado(m, true)}
                        >
                          Desactivar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          title={`Devolver el acceso a ${m.displayName}`}
                          disabled={cambiando}
                          onClick={() => void cambiarEstado(m, false)}
                        >
                          Reactivar
                        </button>
                      ))}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderInvitaciones() {
    if (invitations.length === 0) {
      return (
        <EmptyState
          title="Sin invitaciones pendientes"
          description="Las invitaciones aceptadas pasan a Miembros."
        />
      );
    }
    if (shownInvitations.length === 0) {
      return (
        <EmptyState title="Ninguna invitación coincide" description="Prueba con otro correo." />
      );
    }
    return (
      <table className="dtable">
        <caption className="sr-only">Invitaciones pendientes</caption>
        <thead>
          <tr>
            <th scope="col">Correo</th>
            <th scope="col">Rol</th>
            <th scope="col">Acceso</th>
            {/* fitness-allow: postventa-labels-single-source — cabecera del handoff §3 (fecha de envío de la invitación), no el estado postventa */}
            <th scope="col">Enviada</th>
            <th scope="col">Estado</th>
            {showInvitationActions && (
              <th scope="col">
                <span className="sr-only">Acciones</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {shownInvitations.map((inv) => {
            const puede = canActOnInvitation(inv, me, canManage);
            const cambiando = enVuelo.has(inv.id);
            return (
              <tr key={inv.id}>
                <td>
                  <span className="cell-with-sub">
                    <span className="cell-strong">{invitationEmailLabel(inv)}</span>
                    {inv.inviteeName && <span className="cell-sub">{inv.inviteeName}</span>}
                  </span>
                </td>
                <td>
                  <span className={rolePillClass(recipe, inv.role)}>
                    {roleLabelFor(inv.role, recipe)}
                  </span>
                </td>
                <td>
                  <span className="cell-meta">{invitationAccessLabel(inv, names, w)}</span>
                </td>
                <td>
                  <span className="cell-meta mono">{fmtEnviada(inv.createdAt)}</span>
                </td>
                <td>
                  <span className="st-pill st-pill--info">Pendiente</span>
                </td>
                {showInvitationActions && (
                  <td className="cell-actions">
                    {puede && inv.email && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        title={`Volver a enviar el correo de invitación a ${inv.email}`}
                        disabled={cambiando}
                        onClick={() => void reenviar(inv)}
                      >
                        <RefreshIcon s={12} /> Reenviar
                      </button>
                    )}
                    {puede && (
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        title="Anular esta invitación; el enlace deja de servir"
                        disabled={cambiando}
                        onClick={() => void revocar(inv)}
                      >
                        Revocar
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderRoles() {
    return (
      <table className="dtable">
        <caption className="sr-only">Roles de la organización</caption>
        <thead>
          <tr>
            <th scope="col">Rol</th>
            <th scope="col">Qué ve</th>
            <th scope="col">Quién lo puede invitar</th>
          </tr>
        </thead>
        <tbody>
          {roleRows.map((r) => (
            <tr key={r.key}>
              <td className="cell-strong">{r.label}</td>
              <td>{r.sees}</td>
              <td>
                <span className="cell-meta">{r.inviters}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function renderBody() {
    if (gate.kind === "error") {
      return gate.offline ? (
        <PageError
          title="Sin conexión"
          description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
          onRetry={gate.retry}
        />
      ) : (
        <PageError
          title="No pudimos cargar tu equipo"
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={gate.retry}
        />
      );
    }
    if (gate.kind !== "ready") {
      return (
        <>
          <PageSkeleton
            label="Cargando tu equipo…"
            visible={gate.kind === "loading" && gate.visible}
          >
            <EquipoTabsSkeleton />
          </PageSkeleton>
          {gate.kind === "loading" && gate.slow && <SlowNotice onRetry={retryAll} />}
        </>
      );
    }
    return (
      <>
        <RefreshNotices
          refreshFailed={gate.refreshFailed}
          revalidatingHint={gate.revalidatingHint}
          onRetry={retryAll}
        />
        <Panel flush>
          <div className="filters">
            <Tabs<Tab>
              value={tab}
              onChange={(next) => {
                setTab(next);
                setQ("");
              }}
              idBase={TABS_ID}
              label="Secciones de equipo"
              tabs={[
                { value: "miembros", label: "Miembros", count: visibleMembers.length },
                {
                  value: "invitaciones",
                  label: "Invitaciones pendientes",
                  count: invitations.length,
                },
                { value: "roles", label: "Roles" },
              ]}
            />
            <div className="filter-spacer" />
            {tab !== "roles" && (
              <div className="filter-search">
                <Icon.Search s={14} />
                <input
                  type="text"
                  value={q}
                  aria-label="Buscar por nombre o correo"
                  placeholder="Buscar por nombre o correo"
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            )}
          </div>
          {tab === "miembros" && (
            <div {...tabPanelProps(TABS_ID, "miembros")}>{renderMiembros()}</div>
          )}
          {tab === "invitaciones" && (
            <div {...tabPanelProps(TABS_ID, "invitaciones")}>{renderInvitaciones()}</div>
          )}
          {tab === "roles" && <div {...tabPanelProps(TABS_ID, "roles")}>{renderRoles()}</div>}
        </Panel>
      </>
    );
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow={words.module}
        title={words.sections.equipo}
        sub="Invita personas, define qué ven y quita accesos cuando alguien deja la empresa."
        actions={
          canInvite && (
            <>
              <button
                type="button"
                className="btn btn--secondary"
                title="Subir una planilla con varias personas a la vez"
                disabled={recipe === undefined}
                onClick={() => setLote(true)}
              >
                <UploadIcon s={14} /> Invitar en lote
              </button>
              <button
                type="button"
                className="btn btn--primary"
                title="Abre el panel para invitar a una o varias personas a tu equipo"
                disabled={recipe === undefined}
                onClick={() => setInvitar(true)}
              >
                <Icon.Plus s={14} /> Invitar persona
              </button>
            </>
          )
        }
      />
      {renderBody()}

      {invitar && recipe && (
        <InvitarDrawer
          recipe={recipe}
          words={w}
          inviterRole={platformAdmin ? null : (role ?? null)}
          platformAdmin={platformAdmin}
          brandCount={orgQuery.data?.brandCount ?? null}
          orgBrands={orgBrands}
          storesQuery={storesQuery}
          onClose={() => setInvitar(false)}
          onDone={invitacionesEnviadas}
        />
      )}
      {lote && recipe && (
        <InvitarLoteDrawer words={w} onClose={() => setLote(false)} onDone={loteEnviado} />
      )}
      {acceso && recipe && (
        <AccesoDrawer
          member={acceso}
          recipe={recipe}
          words={w}
          currentAssetIds={accesoAssetIds}
          names={names}
          storesQuery={storesQuery}
          orgBrands={orgBrands}
          affiliationBrandId={affiliationByUser.get(acceso.userId)}
          canEditAffiliation={canEditAffiliation}
          mode={storePickerMode(recipe, acceso.role)}
          onClose={() => setAcceso(null)}
          onSaved={saved}
        />
      )}
    </div>
  );
}

function EquipoTabsSkeleton() {
  return (
    <div className="panel">
      <div className="filters" aria-hidden="true">
        <Skel width={110} height={30} />
        <Skel width={190} height={30} />
        <Skel width={70} height={30} />
        <div className="filter-spacer" />
        <Skel width={220} height={30} />
      </div>
      <SkelTable rows={6} cols={MEMBER_HEADERS.length} headers={MEMBER_HEADERS} />
    </div>
  );
}
